import { useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { ReviewReasonCategory } from 'src/api/endpoints';
import { useOutputs, usePrenotas, useReviews, useSubmitReview } from 'src/api/queries';
import { AbsenceState } from 'src/components/editorial/AbsenceState';
import { ClaimIdChip } from 'src/components/editorial/ClaimIdChip';
import { DiagnosticList } from 'src/components/editorial/DiagnosticList';
import { ShareLink } from 'src/components/editorial/ShareLink';
import { Badge } from 'src/components/ui/Badge';
import { Button } from 'src/components/ui/Button';
import { Disclosure } from 'src/components/ui/Disclosure';
import { Icon } from 'src/components/ui/Icon';
import { Notice } from 'src/components/ui/Notice';
import { Skeleton } from 'src/components/ui/Skeleton';
import { OUTPUT_STATUS } from 'src/lib/diagnostics';
import { notaProviderLabel, outputMetrics, readerConfig } from 'src/lib/reader';
import { formatDateTime } from 'src/lib/time';
import { outputStatusTone, REVIEW_ACTION_TONE } from 'src/lib/tone';
import type { EditorialOutput, EditorialPrenota, EditorialReview } from 'src/types/editorial';
import { LoadFailure } from 'src/components/editorial/LoadFailure';

/**
 * Answers: what would we publish, and is every sentence backed?
 *
 * The drafts are an accordion, not tabs. Nine profiles of the same story produce nine texts whose
 * headlines are often near-identical, so a tab strip gives an editor no way to tell them apart
 * without opening each one. Every row carries its own orientation, status and metrics, so the
 * differences are visible before anything is expanded — and more than one can stay open for
 * side-by-side reading (Composition §8).
 */
export function NotesWorkspace() {
  const { streamId = '' } = useParams<{ streamId: string }>();
  const outputs = useOutputs(streamId);
  const prenotas = usePrenotas(streamId);
  const reviews = useReviews(streamId);

  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState<'pipeline' | 'drafts'>('pipeline');
  const [onlyBlocked, setOnlyBlocked] = useState(false);

  const byStory = useMemo(() => groupByStory(outputs.data?.outputs ?? []), [outputs.data]);
  const latestReviewByOutput = useMemo(() => {
    const latest = new Map<string, EditorialReview>();
    for (const review of reviews.data?.reviews ?? []) {
      const current = latest.get(review.transformation_id);
      if (!current || Date.parse(review.reviewed_at) > Date.parse(current.reviewed_at)) {
        latest.set(review.transformation_id, review);
      }
    }
    return latest;
  }, [reviews.data]);
  const profileCount = useMemo(
    () => new Set((outputs.data?.outputs ?? []).map(output => output.profileId)).size,
    [outputs.data]
  );
  const notApprovedCount = outputs.data && reviews.data
    ? outputs.data.outputs.filter(output => {
        const action = latestReviewByOutput.get(output.transformationId)?.action;
        return action !== 'approved' && action !== 'published';
      }).length
    : null;

  const prenotaById = useMemo(
    () => new Map((prenotas.data?.prenotas ?? []).map(prenota => [prenota.storyId, prenota])),
    [prenotas.data]
  );

  const needle = query.trim().toLowerCase();
  const filtering = needle !== '' || onlyBlocked || sortBy !== 'pipeline';
  const visibleStories = useMemo(() => {
    let stories = byStory;
    if (needle) {
      stories = stories.filter(
        ([storyId, drafts]) =>
          storyId.toLowerCase().includes(needle) ||
          drafts.some(
            draft =>
              draft.profileId.toLowerCase().includes(needle) ||
              (draft.payload.content?.headline ?? '').toLowerCase().includes(needle)
          )
      );
    }
    if (onlyBlocked) {
      stories = stories.filter(
        ([storyId, drafts]) =>
          (prenotaById.get(storyId)?.payload.eligibility?.blocking_codes.length ?? 0) > 0 ||
          drafts.some(draft => (draft.payload.validation?.blocking_notes?.length ?? 0) > 0)
      );
    }
    if (sortBy === 'drafts') {
      stories = [...stories].sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));
    }
    return stories;
  }, [byStory, needle, onlyBlocked, sortBy, prenotaById]);

  // The section shell renders in every state — pending, error, empty — so the page never falls
  // back to a bare panel without its heading.
  return (
    <div className="page-medium">
      <header className="workspace-section-head notes-section-head" aria-labelledby="notes-title">
        <div className="workspace-section-head__copy">
          <p className="workspace-section-head__eyebrow">06 <span aria-hidden="true">/</span> REDACCIÓN Y REVISIÓN</p>
          <h2 id="notes-title">Notas y revisión</h2>
          <p>
            Compara los borradores por perfil editorial. La validación automática y la aprobación de una persona son
            pasos distintos: abre una versión para leer su respaldo y registrar el dictamen.
          </p>
        </div>
        <div className="workspace-section-head__stats" aria-label="Resumen de notas y revisión">
          <div className="workspace-section-head__stat">
            <strong>{outputs.data ? byStory.length : '—'}</strong>
            <span>historias</span>
          </div>
          <div className="workspace-section-head__stat">
            <strong>{outputs.data ? outputs.data.outputs.length : '—'}</strong>
            <span>borradores</span>
          </div>
          <div className="workspace-section-head__stat">
            <strong>{notApprovedCount ?? '—'}</strong>
            <span>sin aprobación</span>
          </div>
          <div className="workspace-section-head__stat">
            <strong>{outputs.data ? profileCount : '—'}</strong>
            <span>perfiles editoriales</span>
          </div>
        </div>
      </header>
      {filtering && outputs.data && (
        <p className="notes-filter-count" role="status">
          Mostrando {visibleStories.length} de {byStory.length} historias
        </p>
      )}

      <section className="tx-filters" aria-label="Filtros de borradores">
        <div className="tx-field tx-search">
          <label htmlFor="notes-q">Buscar</label>
          <div className="tx-search-box">
            <span className="tx-search-icon" aria-hidden="true">
              <Icon name="search" />
            </span>
            <input
              id="notes-q"
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Buscar historia o perfil"
              autoComplete="off"
            />
          </div>
        </div>
        <div className="tx-field tx-field--select">
          <label htmlFor="notes-sort">Ordenar</label>
          <select id="notes-sort" value={sortBy} onChange={event => setSortBy(event.target.value as 'pipeline' | 'drafts')}>
            <option value="pipeline">Por pipeline</option>
            <option value="drafts">Por borradores</option>
          </select>
        </div>
        <label className="tx-check">
          <input type="checkbox" checked={onlyBlocked} onChange={event => setOnlyBlocked(event.target.checked)} />
          <span>Solo bloqueadas</span>
        </label>
        <button
          className="tx-clear"
          type="button"
          onClick={() => {
            setQuery('');
            setSortBy('pipeline');
            setOnlyBlocked(false);
          }}
          disabled={!filtering}
        >
          Limpiar
        </button>
      </section>

      {outputs.isPending ? (
        <Skeleton variant="rows" count={6} />
      ) : outputs.isError ? (
        <LoadFailure title="No se pudieron cargar las notas" error={outputs.error} onRetry={outputs.refetch} />
      ) : byStory.length === 0 ? (
        <AbsenceState
          kind="skipped"
          what="No hay borradores para esta transmisión."
          why="La redacción automática exige un score de X ≥ 65. Una historia que X revisó y puntuó por debajo de ese piso no se redacta."
          wouldChange="Si X no respondió, la compuerta no se aplica y la historia se redacta igual, marcada sin relevancia medida. Que no haya nada aquí significa que sí hubo puntajes y ninguno alcanzó el piso."
        />
      ) : visibleStories.length === 0 ? (
        <p className="guion-empty">Ninguna historia coincide con los filtros.</p>
      ) : (
        visibleStories.map(([storyId, drafts]) => (
          <StorySection
            key={storyId}
            storyId={storyId}
            streamId={streamId}
            drafts={drafts}
            prenota={prenotaById.get(storyId)}
            reviews={(reviews.data?.reviews ?? []).filter(review => review.story_id === storyId)}
          />
        ))
      )}
    </div>
  );
}

function StorySection({
  storyId,
  streamId,
  drafts,
  prenota,
  reviews,
}: {
  storyId: string;
  streamId: string;
  drafts: EditorialOutput[];
  prenota: EditorialPrenota | undefined;
  reviews: EditorialReview[];
}) {
  // Everything starts collapsed. The closed row already carries orientation, word count, cited
  // ratio and fidelity, so the comparison an editor came to make happens in the list itself —
  // auto-opening one version just buries the other rows under it.
  const [open, setOpen] = useState<Set<string>>(new Set());

  function toggle(transformationId: string) {
    setOpen(current => {
      const next = new Set(current);
      if (next.has(transformationId)) next.delete(transformationId);
      else next.add(transformationId);
      return next;
    });
  }

  const headline = drafts.find(draft => draft.payload.content)?.payload.content?.headline ?? storyId;
  const first = drafts[0];
  const runs = indexRuns(drafts);
  const profileCount = new Set(drafts.map(draft => draft.profileId)).size;

  return (
    <section className="story-notes" id={`story-${storyId}`}>
      <header className="story-notes__head">
        <h2 className="story-notes__headline">{headline}</h2>
        <p className="story-notes__meta">
          {drafts.length} {drafts.length === 1 ? 'borrador' : 'borradores'}
          {' · '}
          {profileCount} {profileCount === 1 ? 'perfil editorial' : 'perfiles editoriales'}
        </p>
        {first && drafts.length > 1 && (
          <p className="story-notes__actions">
            <Link to={`/nota/${encodeURIComponent(streamId)}/${encodeURIComponent(first.transformationId)}/comparar`}>
              Comparar las {drafts.length} versiones →
            </Link>
          </p>
        )}
      </header>

      {/* The prenota's blocking codes explain why some of these drafts look the way they do. */}
      {prenota && (prenota.payload.eligibility?.blocking_codes.length ?? 0) > 0 && (
        <DiagnosticList
          codes={prenota.payload.eligibility?.blocking_codes ?? []}
          title="Por qué la historia de origen está bloqueada"
        />
      )}

      <div className="accordion">
        {drafts.map(draft => (
          <DraftRow
            key={draft.transformationId}
            output={draft}
            streamId={streamId}
            run={runs.get(draft.transformationId)}
            open={open.has(draft.transformationId)}
            onToggle={() => toggle(draft.transformationId)}
            reviews={reviews.filter(review => review.transformation_id === draft.transformationId)}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * Which run of a profile each version is.
 *
 * A story re-drafted four times shows four rows for the same profile, and when their metrics
 * coincide — which they do whenever the drafter produced nothing — the rows are indistinguishable.
 * Chronological within the profile, so the label says "the third attempt" rather than ranking them.
 */
function indexRuns(drafts: EditorialOutput[]): Map<string, { index: number; total: number; latest: boolean }> {
  const byProfile = new Map<string, EditorialOutput[]>();
  for (const draft of drafts) {
    const bucket = byProfile.get(draft.profileId);
    if (bucket) bucket.push(draft);
    else byProfile.set(draft.profileId, [draft]);
  }

  const runs = new Map<string, { index: number; total: number; latest: boolean }>();
  for (const versions of byProfile.values()) {
    if (versions.length < 2) continue; // one version of a profile needs no run number
    const ordered = [...versions].sort(
      (a, b) => Date.parse(a.payload.generated_at || '') - Date.parse(b.payload.generated_at || '')
    );
    ordered.forEach((draft, position) => {
      runs.set(draft.transformationId, {
        index: position + 1,
        total: ordered.length,
        latest: position === ordered.length - 1,
      });
    });
  }
  return runs;
}

function DraftRow({
  output,
  streamId,
  run,
  open,
  onToggle,
  reviews,
}: {
  output: EditorialOutput;
  streamId: string;
  /** Absent when this profile drafted the story once — then there is nothing to disambiguate. */
  run?: { index: number; total: number; latest: boolean };
  open: boolean;
  onToggle: () => void;
  reviews: EditorialReview[];
}) {
  const payload = output.payload;
  const config = readerConfig(output);
  const metrics = outputMetrics(output);
  const status = OUTPUT_STATUS[payload.status];
  const latestReview = [...reviews].sort((a, b) => Date.parse(b.reviewed_at) - Date.parse(a.reviewed_at))[0];
  const humanReview = latestReview
    ? {
        label: {
          approved: 'Aprobada',
          published: 'Publicada',
          edited: 'Editada · falta aprobar',
          rejected: 'Rechazada',
        }[latestReview.action],
        tone: REVIEW_ACTION_TONE[latestReview.action],
      }
    : { label: 'Pendiente de revisión', tone: 'unresolved' as const };
  const panelId = `panel-${output.transformationId}`;
  // Headings don't cite — `outputMetrics` already excludes them from the prose count, and
  // counting them here would flag every section title as "unbacked" (see the block renderer).
  const unbacked = (payload.content?.body_blocks ?? []).filter(
    block =>
      block.type !== 'heading' && (block.claim_ids ?? []).length === 0 && (block.fact_ids ?? []).length === 0
  ).length;

  // A draft's body can be long and there are up to nine per story — mounting all of them for the
  // close-animation grid track would render every profile's full text up front. Mount once a row
  // has been opened, and keep it mounted after that so later toggles still animate (transitions.dev §21).
  const everOpened = useRef(open);
  if (open) everOpened.current = true;

  return (
    <article className="accordion__item" data-open={open} id={`draft-${output.transformationId}`}>
      <h3 className="accordion__heading">
        <button type="button" className="accordion__trigger" aria-expanded={open} aria-controls={panelId} onClick={onToggle}>
          <span className="accordion__chevron" aria-hidden="true" />
          {/* Each row carries its own identity, so the versions are distinguishable while closed. */}
          <span className="accordion__dot" style={{ background: config.color }} aria-hidden="true" />
          <span className="accordion__identity">
            <span className="accordion__profile">{config.treatment || config.label}</span>
            <span className="accordion__orientation">
              {config.treatment ? config.label : 'Perfil editorial'}
              {config.intensity && ` · tono ${config.intensity}`}
            </span>
            {run && (
              <span className="accordion__run">
                {run.latest ? 'última corrida' : `corrida ${run.index} de ${run.total}`}
                {' · '}
                {formatDateTime(output.payload.generated_at)}
                {output.payload.reused_execution && ' · reusada de caché'}
              </span>
            )}
          </span>
          <span className="accordion__facts">
            <span className="accordion__fact">
              <b>{metrics.words}</b> palabras
            </span>
            <span
              className="accordion__fact"
              title="Bloques de texto con al menos un claim o hecho externo citado, sobre el total de bloques de texto."
            >
              <b>
                {metrics.cited}/{metrics.prose}
              </b>{' '}
              bloques con respaldo
            </span>
            {metrics.fidelity !== null && (
              <span
                className="accordion__fact"
                title="Porcentaje de hallazgos evaluados que el validador automático clasificó como respaldados."
              >
                <b>{metrics.fidelity}%</b> hallazgos respaldados
              </span>
            )}
          </span>
          {/* The gate did not run for this one. Saying so on the closed row keeps an editor from
              reading its presence as evidence that anything ranked it. */}
          <span className="accordion__status-group">
            <Badge tone={outputStatusTone(payload.status)}>{status?.label ?? payload.status}</Badge>
            <Badge tone={humanReview.tone}>{humanReview.label}</Badge>
            {payload.generation?.used_external_model === false && <Badge tone="unresolved">Escrita en local</Badge>}
            {payload.relevance_unverified && <Badge tone="unresolved">Sin relevancia medida</Badge>}
          </span>
        </button>
      </h3>

      {everOpened.current && (
        <div className="accordion__panel" id={panelId} aria-hidden={!open}>
          <div className="accordion__panel-inner">
            {status && (
              <p className="draft-status" data-tone={outputStatusTone(payload.status)}>
                <span className="draft-status__dot" aria-hidden="true" />
                {status.hint}
              </p>
            )}

            {payload.generation?.used_external_model === false && (
              <Notice tone="unresolved" title="La escribió el modelo local">
                {payload.generation.provider_note ||
                  'Ningún proveedor de pago respondió cuando tocaba redactar esta historia.'}{' '}
                Sale más corta y con menos contexto externo que la misma historia escrita por un modelo de
                pago; los hechos y las citas siguen saliendo de la transmisión.
              </Notice>
            )}

            {payload.relevance_unverified && (
              <Notice tone="unresolved" title="Sin relevancia medida">
                X no respondió cuando le tocaba puntuar esta historia, así que se redactó sin pasar por la compuerta
                de relevancia. El texto y sus citas son los de siempre; lo que falta es la señal de cuánto se está
                hablando del tema afuera.
              </Notice>
            )}

            <ShareLink path={`/nota/${encodeURIComponent(streamId)}/${encodeURIComponent(output.transformationId)}`} />

            <div className="draft__reader-links">
              <Link className="btn btn--secondary" to={`/nota/${encodeURIComponent(streamId)}/${encodeURIComponent(output.transformationId)}`}>
                Abrir como nota →
              </Link>
              <span className="draft__generated mono">{formatDateTime(payload.generated_at)}</span>
            </div>

            <DraftBody output={output} unbacked={unbacked} />
            <DraftReviews output={output} reviews={reviews} />
          </div>
        </div>
      )}
    </article>
  );
}

function DraftBody({ output, unbacked }: { output: EditorialOutput; unbacked: number }) {
  const payload = output.payload;
  const content = payload.content;
  const usedClaimIds = payload.traceability?.used_claim_ids ?? [];
  const usedFactIds = payload.traceability?.used_external_fact_ids ?? [];
  const publishedSourceIds = payload.traceability?.published_source_ids ?? [];
  const omittedClaims = payload.traceability?.omitted_claims ?? [];
  const generation = payload.generation;
  const citationsCheck = payload.validation?.citations;
  const verificationRef = payload.verification_reference;

  // Every reason the pipeline gave for this draft's state, translated in one place.
  const codes = [
    ...(payload.validation?.blocking_notes ?? []).map(note => note.problem ?? note.classification ?? note.field ?? ''),
    ...(payload.validation?.revision?.result === 'revalidation_failed' ? ['revalidation_failed'] : []),
  ].filter(Boolean);

  if (!content) {
    return (
      <>
        <AbsenceState
          kind="failed"
          what="Este perfil no produjo texto."
          why={OUTPUT_STATUS[payload.status]?.hint ?? `El pipeline lo cerró con estado ${payload.status}.`}
        />
        <DiagnosticList codes={codes} title="Qué falló" />
      </>
    );
  }

  let proseIndex = 0;
  const proseBlockCount = (content.body_blocks ?? []).filter(block => block.type !== 'heading').length;
  return (
    <>
      <DiagnosticList codes={codes} title="Qué bloquea esta versión" />

      <div className="draft-preview">
        <h4 className="draft__headline">{content.headline}</h4>
        <p className="draft__deck">{content.deck}</p>

        {unbacked > 0 && (
          <p className="draft__unbacked-summary">
            {unbacked} {unbacked === 1 ? 'párrafo no cita' : 'párrafos no citan'} ningún claim. No siempre es un error —
            las transiciones y el encuadre no tienen por qué citar — pero es lo primero que conviene leer.
          </p>
        )}

        <div className="draft__body-heading">
          <span>Contenido de la nota</span>
          <span>{proseBlockCount} {proseBlockCount === 1 ? 'bloque' : 'bloques'}</span>
        </div>
        <div className="draft__body">
          {(content.body_blocks ?? []).map((block, index) => {
            const claimIds = block.claim_ids ?? [];
            const factIds = block.fact_ids ?? [];
            const citations = block.citations ?? [];
            const backed = claimIds.length > 0 || factIds.length > 0;
            // A heading is structure, not prose — it never needs backing, and marking it
            // "sin claim que lo respalde" reads as a defect that isn't one.
            const isHeading = block.type === 'heading';
            const number = isHeading ? null : ++proseIndex;
            return (
              <div key={index} className="draft__block" data-backed={backed} data-type={block.type}>
                {isHeading ? (
                  <div className="draft__section-marker">
                    <span>SECCIÓN</span>
                    <h5 className="draft__block-heading">{block.text}</h5>
                  </div>
                ) : (
                  <>
                    <div className="draft__block-head">
                      <span className="draft__block-number">Bloque {String(number).padStart(2, '0')}</span>
                      <span className="draft__block-state" data-backed={backed}>
                        <span aria-hidden="true">{backed ? '✓' : '!'}</span>
                        {backed ? 'Con respaldo' : 'Revisar respaldo'}
                      </span>
                    </div>
                    <p className="reading">{block.text}</p>
                    <div className="draft__block-claims">
                      {backed ? (
                        <>
                          <span className="draft__block-support-label">Claims y hechos</span>
                          {claimIds.map(claimId => <ClaimIdChip key={claimId} claimId={claimId} />)}
                          {factIds.map(factId => (
                            <span key={factId} className="fact-chip mono" title="Hecho externo aportado por la verificación">
                              {factId}
                            </span>
                          ))}
                        </>
                      ) : (
                        <span className="draft__unbacked">Sin claims o hechos vinculados</span>
                      )}
                    </div>
                    {citations.length > 0 && (
                      <p className="draft__citations mono">
                        {citations.length} {citations.length === 1 ? 'cita' : 'citas'} a fuente ·{' '}
                        {citations.flatMap(citation => citation.source_ids).join(', ')}
                      </p>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <Disclosure summary="Trazabilidad" count={usedClaimIds.length}>
        <div className="draft__trace-stats">
          <span className="draft__trace-stat">
            <b>{usedClaimIds.length}</b> claims usados
          </span>
          <span className="draft__trace-stat">
            <b>{usedFactIds.length}</b> hechos externos
          </span>
          <span className="draft__trace-stat">
            <b>{omittedClaims.length}</b> omitidos
          </span>
        </div>
        <div className="draft__used">
          {usedClaimIds.map(claimId => (
            <ClaimIdChip key={claimId} claimId={claimId} />
          ))}
          {usedFactIds.map(factId => (
            <span key={factId} className="fact-chip mono">
              {factId}
            </span>
          ))}
        </div>
        {omittedClaims.length > 0 && (
          <ul className="draft__omitted">
            {omittedClaims.map(omission => (
              <li key={omission.claim_id}>
                <ClaimIdChip claimId={omission.claim_id} />
                <span>{omission.reason}</span>
              </li>
            ))}
          </ul>
        )}
        {publishedSourceIds.length > 0 && (
          <p className="draft__trace-line mono">fuentes publicadas: {publishedSourceIds.join(', ')}</p>
        )}
        {citationsCheck && (
          <p className="draft__trace-line">
            <Badge tone={citationsCheck.decision === 'pass' ? 'supported' : 'contradicted'}>
              citas {citationsCheck.decision === 'pass' ? 'sin huérfanas' : 'con huérfanas'}
            </Badge>
            {(citationsCheck.orphan_links ?? 0) > 0 && ` · ${citationsCheck.orphan_links} huérfanas`}
          </p>
        )}
        {verificationRef && (
          <p className="draft__trace-line">
            <Link to={`/stream/${encodeURIComponent(output.streamId)}/verification#verification-${verificationRef.story_key}`}>
              Ver verificación ({verificationRef.status}) →
            </Link>
          </p>
        )}
        {generation && (
          <p className="draft__generation mono">
            {notaProviderLabel(generation.provider, generation.model)} · {generation.model} ·{' '}
            {Math.round((generation.duration_ms ?? 0) / 1000)} s · hash{' '}
            {(generation.input_hash ?? '').slice(0, 12)}
          </p>
        )}
      </Disclosure>
    </>
  );
}

function DraftReviews({ output, reviews }: { output: EditorialOutput; reviews: EditorialReview[] }) {
  return (
    <section className="draft__reviews">
      <h4 className="section-title">Revisión editorial</h4>
      {reviews.length > 0 && (
        <ul className="draft__review-list">
          {reviews.map(review => (
            <li key={review.review_id}>
              <span className="review__head">
                <Badge tone={REVIEW_ACTION_TONE[review.action]}>{review.action}</Badge>
                <span className="mono">{review.editor_id}</span>
                <span className="review__category">{review.reason_category}</span>
                <span className="review__metrics mono">{review.metrics.edit_distance_words} palabras cambiadas</span>
              </span>
              {review.comment && <p className="draft__review-comment">{review.comment}</p>}
            </li>
          ))}
        </ul>
      )}
      <ReviewForm output={output} />
    </section>
  );
}

const REASON_LABEL: Record<ReviewReasonCategory, string> = {
  factual: 'Error factual',
  attribution: 'Atribución',
  style: 'Estilo',
  scope: 'No es nota (alcance)',
  format: 'Formato',
  other: 'Otro',
};

/**
 * The writer half of the learning loop. An approval with zero edits becomes a style exemplar in
 * the next drafting prompt; a rejection categorized as `scope` raises the relevance floor; every
 * verdict feeds the per-stage defect report. Until this form existed those readers had no source.
 */
function ReviewForm({ output }: { output: EditorialOutput }) {
  const review = useSubmitReview(output.streamId);
  const [rejecting, setRejecting] = useState(false);
  const [reasonCategory, setReasonCategory] = useState<ReviewReasonCategory>('factual');
  const [comment, setComment] = useState('');

  if (review.isSuccess) return <p className="review-form__done">Revisión registrada. El pipeline la lee en su próxima pasada de aprendizaje.</p>;

  return (
    <div className="review-form">
      <div className="review-form__actions">
        <Button
          type="button"
          variant="secondary"
          busy={review.isPending && !rejecting}
          disabled={review.isPending}
          onClick={() => review.mutate({ transformationId: output.transformationId, action: 'approved', reasonCategory: 'other' })}
        >
          Aprobar tal cual
        </Button>
        <Button type="button" variant={rejecting ? 'primary' : 'ghost'} disabled={review.isPending} onClick={() => setRejecting(current => !current)}>
          Rechazar…
        </Button>
      </div>

      {/* The loop is invisible without this line. `reviews` held zero rows on 2026-09-23 — the
          form has existed for weeks and nobody had reason to believe pressing it changed anything,
          so the drafting prompt's voice anchors were empty on every broadcast. */}
      <p className="review-form__why">
        Una aprobación sin cambios se vuelve referencia de voz en el prompt de redacción; un rechazo
        dice qué corregir. Sin revisiones, el redactor no aprende de la redacción.
      </p>

      {rejecting && (
        <form
          className="review-form__reject"
          onSubmit={event => {
            event.preventDefault();
            review.mutate({ transformationId: output.transformationId, action: 'rejected', reasonCategory, comment: comment.trim() });
          }}
        >
          <label className="review-form__field">
            <span>Por qué</span>
            <select value={reasonCategory} onChange={event => setReasonCategory(event.target.value as ReviewReasonCategory)}>
              {Object.entries(REASON_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="review-form__field">
            <span>Comentario (opcional)</span>
            <textarea value={comment} onChange={event => setComment(event.target.value)} rows={2} maxLength={2000} />
          </label>
          <Button type="submit" busy={review.isPending} disabled={review.isPending}>
            Confirmar rechazo
          </Button>
        </form>
      )}

      {review.isError && <p className="review-form__error">No se pudo guardar la revisión: {review.error instanceof Error ? review.error.message : 'error desconocido'}</p>}
    </div>
  );
}

/** Stable ordering: stories by id, profiles by id. Never by any metric (Art Direction §14). */
function groupByStory(outputs: EditorialOutput[]): Array<[string, EditorialOutput[]]> {
  const groups = new Map<string, EditorialOutput[]>();
  for (const output of outputs) {
    const bucket = groups.get(output.storyId);
    if (bucket) bucket.push(output);
    else groups.set(output.storyId, [output]);
  }
  for (const drafts of groups.values()) drafts.sort((a, b) => a.profileId.localeCompare(b.profileId));
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}
